/* eslint-disable indent */
import { DynamicPrices } from '@/types'
import { IEdition, IEditionDynamicTrades } from '@/types/IEdition'
import { Currencies, Currency } from 'constants/currencies'
import { Regions } from 'constants/regions'
import { FilterArtworkHitProps, Region } from 'types'
import { pickPrice, PickPricePayload } from 'utils/currencies'
import { flatCollectionType } from 'utils/data'

export function toDollars(value?: number | string | null) {
  const numValue = Number(value) || undefined
  if (!numValue) return 0
  return numValue / 100
}

interface MapDynamicPricesToPickPricePayload {
  userRegion: Region | null
  userCurrency: Currency | null
  prices: Partial<DynamicPrices>
}

/**
 * Maps the dynamic prices to the payload of the `pickPrice` function, based on the user region and the user currency (they can select a currency which is not related to their region).
 */
function mapDynamicPricesToPickPricePayload({
  prices,
  userCurrency = Currencies.GBP,
  userRegion = Regions.UK,
}: MapDynamicPricesToPickPricePayload): PickPricePayload | null {
  if (!userCurrency || !userRegion || !prices) return null

  switch (userRegion) {
    case Regions.UK:
      return {
        price: prices.adjustedPriceUk,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceUs,
        priceCurrency: Currencies.GBP,
        userCurrency,
      }

    case Regions.US:
      return {
        price: prices.adjustedPriceUs,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceUs,
        priceCurrency: Currencies.USD,
        userCurrency,
      }

    case Regions.EU_1:
      return {
        price: prices.adjustedPriceEu1,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceUs,
        priceCurrency: Currencies.EUR,
        userCurrency,
      }

    case Regions.EU_2:
      return {
        price: prices.adjustedPriceEu2,
        priceEur: prices.adjustedPriceEu2,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceUs,
        priceCurrency: Currencies.EUR,
        userCurrency,
      }

    case Regions.CAN_MEX:
      return {
        price: prices.adjustedPriceCanMex,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceCanMex,
        priceCurrency: Currencies.USD,
        userCurrency,
      }

    case Regions.A_PAC:
      return {
        price: prices.adjustedPriceApac,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceApac,
        priceCurrency: Currencies.USD,
        userCurrency,
      }

    case Regions.M_EAST:
      return {
        price: prices.adjustedPriceMeast,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceMeast,
        priceCurrency: Currencies.USD,
        userCurrency,
      }

    case Regions.ROW:
      return {
        price: prices.adjustedPriceRow,
        priceEur: prices.adjustedPriceEu1,
        priceGbp: prices.adjustedPriceUk,
        priceUsd: prices.adjustedPriceRow,
        priceCurrency: Currencies.USD,
        userCurrency,
      }
  }

  return null
}

interface PickDynamicPricePayload {
  userRegion: Region | null
  userCurrency: Currency | null
  prices: Partial<DynamicPrices>
}

/**
 * Picks the price according to the user's region and currency.
 */
export function pickDynamicPrice({
  prices,
  userCurrency,
  userRegion,
}: PickDynamicPricePayload) {
  const pickPricePayload = mapDynamicPricesToPickPricePayload({
    userRegion,
    userCurrency,
    prices,
  })

  if (!pickPricePayload) return null

  return pickPrice(pickPricePayload)
}

export function pickDynamicTradesFromEditionAttributes(
  edition: IEdition['attributes']
): IEditionDynamicTrades {
  return {
    highestBidEu1: edition.highestBidEu1 || null,
    highestBidEu2: edition.highestBidEu2 || null,
    highestBidCanMex: edition.highestBidCanMex || null,
    highestBidMeast: edition.highestBidMeast || null,
    highestBidApac: edition.highestBidApac || null,

    highestBidRow: edition.highestBidRow || null,
    highestBidUk: edition.highestBidUk || null,
    highestBidUs: edition.highestBidUs || null,
    lowestAskApac: edition.lowestAskApac || null,
    lowestAskCanMex: edition.lowestAskCanMex || null,
    lowestAskEu1: edition.lowestAskEu1 || null,
    lowestAskEu2: edition.lowestAskEu2 || null,
    lowestAskMeast: edition.lowestAskMeast || null,
    lowestAskRow: edition.lowestAskRow || null,
    lowestAskUk: edition.lowestAskUk || null,
    lowestAskUs: edition.lowestAskUs || null,
  }
}

interface PickDynamicTradesPayload {
  userRegion: Region | null
  trades: IEditionDynamicTrades
}

export function pickTradesForRegion({
  userRegion = Regions.UK,
  trades,
}: PickDynamicTradesPayload) {
  switch (userRegion) {
    case Regions.UK:
      return { lowestAsk: trades.lowestAskUk, highestBid: trades.highestBidUk }

    case Regions.US:
      return { lowestAsk: trades.lowestAskUs, highestBid: trades.highestBidUs }

    case Regions.EU_1:
      return {
        lowestAsk: trades.lowestAskEu1,
        highestBid: trades.highestBidEu1,
      }

    case Regions.EU_2:
      return {
        lowestAsk: trades.lowestAskEu2,
        highestBid: trades.highestBidEu2,
      }

    case Regions.A_PAC:
      return {
        lowestAsk: trades.lowestAskApac,
        highestBid: trades.highestBidApac,
      }

    case Regions.CAN_MEX:
      return {
        lowestAsk: trades.lowestAskCanMex,
        highestBid: trades.highestBidCanMex,
      }

    case Regions.M_EAST:
      return {
        lowestAsk: trades.lowestAskMeast,
        highestBid: trades.highestBidMeast,
      }

    case Regions.ROW:
      return {
        lowestAsk: trades.lowestAskRow,
        highestBid: trades.highestBidRow,
      }
  }

  return { lowestAsk: null, highestBid: null }
}

interface PickDynamicTradesPricesPayload {
  userRegion: Region | null
  userCurrency: Currency | null
  trades: IEditionDynamicTrades
}

export function pickDynamicTradesPrices({
  trades,
  userCurrency,
  userRegion,
}: PickDynamicTradesPricesPayload) {
  const regionalTrades = pickTradesForRegion({ trades, userRegion })

  const flatHighestBid = regionalTrades.highestBid?.data
    ? flatCollectionType(regionalTrades.highestBid.data)
    : null
  const highestBid = flatHighestBid
    ? pickDynamicPrice({
        userRegion,
        userCurrency,
        prices: flatHighestBid,
      })
    : null

  const flatLowestAsk = regionalTrades.lowestAsk?.data
    ? flatCollectionType(regionalTrades.lowestAsk.data)
    : null
  const lowestAsk = flatLowestAsk
    ? pickDynamicPrice({
        userRegion,
        userCurrency,
        prices: flatLowestAsk,
      })
    : null

  return {
    /**
     * The price of the highest bid correlated to the user's region and currency.
     */
    highestBid,
    /**
     * The base price of the highest bid, converted to GBP.
     */
    highestBidGbp: flatHighestBid?.priceGbp,
    highestBidId: flatHighestBid?.id,
    /**
     * The price of the lowest ask correlated to the user's region and currency.
     */
    lowestAsk,
    lowestAskId: flatLowestAsk?.id,
    /**
     * The base price of the lowest ask, converted to GBP. Doesn't have many uses, apart from calculating the shipping cost.
     */
    lowestAskGbp: flatLowestAsk?.priceGbp,
  }
}

function mapAlgoliaHitPricesToDynamicPrices(hit: FilterArtworkHitProps): {
  lowestAsk: Partial<DynamicPrices>
  highestBid: Partial<DynamicPrices>
} {
  return {
    lowestAsk: {
      adjustedPriceApac: hit.lowestAskApacPrice,
      adjustedPriceCanMex: hit.lowestAskCanMexPrice,
      adjustedPriceEu1: hit.lowestAskEu1Price,
      adjustedPriceEu2: hit.lowestAskEu2Price,
      adjustedPriceMeast: hit.lowestAskMeastPrice,
      adjustedPriceRow: hit.lowestAskRowPrice,
      adjustedPriceUk: hit.lowestAskUkPrice,
      adjustedPriceUs: hit.lowestAskUsPrice,
    },
    highestBid: {
      adjustedPriceRow: hit.highestBidRowPrice,
      adjustedPriceUk: hit.highestBidUkPrice,
      adjustedPriceUs: hit.highestBidUsPrice,
      adjustedPriceCanMex: hit.highestBidCanMexPrice,
      adjustedPriceEu1: hit.highestBidEu1Price,
      adjustedPriceEu2: hit.highestBidEu2Price,
      adjustedPriceMeast: hit.highestBidMeastPrice,
      adjustedPriceApac: hit.highestBidApacPrice,
    },
  }
}

interface PickDynamicPricesFromAlgoliaHitPayload {
  hit: FilterArtworkHitProps
  userCurrency: Currency | null
  userRegion: Region | null
}

export function pickDynamicPricesFromAlgoliaHit({
  hit,
  userCurrency,
  userRegion,
}: PickDynamicPricesFromAlgoliaHitPayload) {
  const mappedPrices = mapAlgoliaHitPricesToDynamicPrices(hit)

  const highestBid = pickDynamicPrice({
    userCurrency,
    userRegion,
    prices: mappedPrices.highestBid,
  })
  const lowestAsk = pickDynamicPrice({
    userCurrency,
    userRegion,
    prices: mappedPrices.lowestAsk,
  })

  return { highestBid, lowestAsk }
}
